import React from "react";
import { w2p, izoom, mm2m } from "../../utils/coords";
import { Geo } from "../../utils/geo";

/**
 * AnnotationLayer — renders text annotations, manual dimensions, and grid lines.
 *
 * Improvements:
 *  - Annotations support custom fontSize from ann.fontSize
 *  - Annotations use JetBrains Mono (consistent with app)
 *  - Better callout box: auto-sized to text length
 *  - Manual dims: cleaner tick/extension line rendering
 *  - Grid lines: correct bubble at both ends
 *  - All elements have larger transparent hit targets for easier selection
 *  - Drag support on annotations (onDragStart prop)
 */
export default function AnnotationLayer({ st, selId, zoom, onSelect, onDragStart, dragAnnId, dragAnnPos }) {
  const s     = izoom(zoom);
  const anns  = Object.values(st.annotations || {});
  const dims  = Object.values(st.manualDims  || {});
  const gls   = Object.values(st.gridLines   || {});

  const annLayer = (st.layers || {}).annotations;
  const dimLayer = (st.layers || {}).dimensions;
  const strLayer = (st.layers || {}).structural;

  const FONT = "'JetBrains Mono','Fira Code',monospace";

  return (
    <g>
      {/* ── Structural grid lines ──────────────────────────────────────────── */}
      {strLayer?.visible !== false && gls.map(gl => {
        const isSel = selId === gl.id;
        const px    = w2p(gl.position);
        const isX   = gl.axis === "x";
        const col   = isSel ? "#e8a838" : "#f97316";
        const bubbleR = s * 9;

        const ns = Object.values(st.nodes || {});
        const coords = ns.map(n => isX ? n.y : n.x);
        const minC = coords.length ? Math.min(...coords) - 800 : -2000;
        const maxC = coords.length ? Math.max(...coords) + 800 :  8000;

        const lx1 = isX ? px : w2p(minC), ly1 = isX ? w2p(minC) : px;
        const lx2 = isX ? px : w2p(maxC), ly2 = isX ? w2p(maxC) : px;

        // Bubble positions — both ends
        const bAx = isX ? px : w2p(minC) - bubbleR * 2;
        const bAy = isX ? w2p(minC) - bubbleR * 2 : px;
        const bBx = isX ? px : w2p(maxC) + bubbleR * 2;
        const bBy = isX ? w2p(maxC) + bubbleR * 2 : px;

        return (
          <g key={gl.id} style={{ cursor:"pointer" }}
            onClick={ev => { ev.stopPropagation(); onSelect?.(gl.id); }}>
            {/* Invisible thick hit target */}
            <line x1={lx1} y1={ly1} x2={lx2} y2={ly2}
              stroke="transparent" strokeWidth={s * 16}/>
            {/* Dash-dot line */}
            <line x1={lx1} y1={ly1} x2={lx2} y2={ly2}
              stroke={col} strokeWidth={s * 0.5} opacity={0.65}
              strokeDasharray={`${s*10} ${s*3} ${s*2} ${s*3}`}/>
            {/* Bubbles */}
            {[{ bx:bAx, by:bAy }, { bx:bBx, by:bBy }].map(({bx,by},i) => (
              <g key={i}>
                <circle cx={bx} cy={by} r={bubbleR}
                  fill="#020a14" stroke={col} strokeWidth={s * 0.6}/>
                <text x={bx} y={by + s * 3.5}
                  textAnchor="middle" fontSize={s * 7}
                  fontFamily={FONT} fontWeight="700" fill={col}
                  style={{ pointerEvents:"none" }}>
                  {gl.label}
                </text>
              </g>
            ))}
          </g>
        );
      })}

      {/* ── Manual dimensions ──────────────────────────────────────────────── */}
      {dimLayer?.visible !== false && dims.map(dim => {
        // Angular dimension
        if (dim.type === "angular") {
          const cx = w2p(dim.ptCenter.x), cy = w2p(dim.ptCenter.y);
          const ax = w2p(dim.ptA.x),      ay = w2p(dim.ptA.y);
          const bx = w2p(dim.ptB.x),      by = w2p(dim.ptB.y);
          const isSel = selId === `dim_${dim.id}`;
          const col   = isSel ? "#e8a838" : "#818cf8";
          const off   = s * Math.max(dim.offset || 800, 100);

          // Angle from center to each point
          const angA = Math.atan2(ay - cy, ax - cx);
          const angB = Math.atan2(by - cy, bx - cx);
          // Arc radius
          const rA = Math.sqrt((ax-cx)**2 + (ay-cy)**2);
          const rB = Math.sqrt((bx-cx)**2 + (by-cy)**2);
          const r  = Math.min(rA, rB) * 0.6 + off * 0.5;
          // Arc path (SVG arc)
          const startX = cx + r * Math.cos(angA);
          const startY = cy + r * Math.sin(angA);
          const endX   = cx + r * Math.cos(angB);
          const endY   = cy + r * Math.sin(angB);
          // Determine large-arc-flag
          let dAng = angB - angA;
          if (dAng < 0) dAng += 2 * Math.PI;
          const largeArc = dAng > Math.PI ? 1 : 0;
          const sweep    = 1;
          // Angle text
          const angleDeg = Math.round(dAng * 180 / Math.PI);
          const text = dim.text || `${angleDeg}°`;
          // Mid-arc label position
          const midAng = angA + dAng / 2;
          const lx = cx + (r + s * 12) * Math.cos(midAng);
          const ly = cy + (r + s * 12) * Math.sin(midAng);

          return (
            <g key={dim.id} style={{ cursor:"pointer" }}
              onClick={ev => { ev.stopPropagation(); onSelect?.("dim_" + dim.id); }}>
              {/* Radial extension lines */}
              <line x1={cx} y1={cy} x2={startX} y2={startY} stroke={col} strokeWidth={s*0.5} opacity={0.5}/>
              <line x1={cx} y1={cy} x2={endX}   y2={endY}   stroke={col} strokeWidth={s*0.5} opacity={0.5}/>
              {/* Arc */}
              <path d={`M ${startX} ${startY} A ${r} ${r} 0 ${largeArc} ${sweep} ${endX} ${endY}`}
                fill="none" stroke={col} strokeWidth={s * 0.8}/>
              {/* Label */}
              <rect x={lx - s*16} y={ly - s*6} width={s*32} height={s*12} rx={s}
                fill="#020a14" stroke={isSel?"#e8a838":"#1e2a36"} strokeWidth={s*0.4}/>
              <text x={lx} y={ly + s*4} textAnchor="middle" fontSize={s*8}
                fontFamily={FONT} fill={col} style={{ pointerEvents:"none" }}>
                {text}
              </text>
            </g>
          );
        }

        // Linear dimension (original code)
        const ax = w2p(dim.ptA.x), ay = w2p(dim.ptA.y);
        const bx = w2p(dim.ptB.x), by = w2p(dim.ptB.y);
        const dist = Geo.d(dim.ptA, dim.ptB);
        const text = dim.text || (dist >= 1000 ? mm2m(dist) + "m" : Math.round(dist) + "mm");
        const off  = s * Math.max(dim.offset || 20, 5);
        const isSel = selId === `dim_${dim.id}`;
        const col  = isSel ? "#e8a838" : "#818cf8";

        const dx = bx - ax, dy = by - ay, L = Math.sqrt(dx*dx + dy*dy) || 1;
        const nx = -dy / L, ny = dx / L;

        const dAx = ax + nx * off, dAy = ay + ny * off;
        const dBx = bx + nx * off, dBy = by + ny * off;
        const mx  = (dAx + dBx) / 2, my = (dAy + dBy) / 2;

        const tkL = s * 5;
        const ang = Math.atan2(dy, dx) * 180 / Math.PI;

        return (
          <g key={dim.id} style={{ cursor:"pointer" }}
            onClick={ev => { ev.stopPropagation(); onSelect?.("dim_" + dim.id); }}>
            <line x1={dAx} y1={dAy} x2={dBx} y2={dBy}
              stroke="transparent" strokeWidth={s * 12}/>
            <line x1={ax} y1={ay} x2={dAx + nx*s*2} y2={dAy + ny*s*2}
              stroke={col} strokeWidth={s * 0.5} opacity={0.6}/>
            <line x1={bx} y1={by} x2={dBx + nx*s*2} y2={dBy + ny*s*2}
              stroke={col} strokeWidth={s * 0.5} opacity={0.6}/>
            <line x1={dAx} y1={dAy} x2={dBx} y2={dBy}
              stroke={col} strokeWidth={s * 0.7}/>
            <line x1={dAx - nx*tkL*0.5} y1={dAy - ny*tkL*0.5}
                  x2={dAx + nx*tkL*0.5} y2={dAy + ny*tkL*0.5}
              stroke={col} strokeWidth={s * 0.9}/>
            <line x1={dBx - nx*tkL*0.5} y1={dBy - ny*tkL*0.5}
                  x2={dBx + nx*tkL*0.5} y2={dBy + ny*tkL*0.5}
              stroke={col} strokeWidth={s * 0.9}/>
            <rect x={mx - s*20} y={my - s*6} width={s*40} height={s*12} rx={s}
              fill="#020a14" stroke={isSel?"#e8a838":"#1e2a36"} strokeWidth={s*0.4}
              transform={`rotate(${ang},${mx},${my})`}/>
            <text x={mx} y={my + s*4}
              textAnchor="middle" fontSize={s * 8}
              fontFamily={FONT} fill={col}
              style={{ pointerEvents:"none" }}
              transform={`rotate(${ang},${mx},${my})`}>
              {text}
            </text>
          </g>
        );
      })}

      {/* ── Text annotations ───────────────────────────────────────────────── */}
      {annLayer?.visible !== false && anns.map(ann => {
        const livePos = (dragAnnId === `ann_${ann.id}` && dragAnnPos) ? dragAnnPos : ann;
        const ax    = w2p(livePos.x), ay = w2p(livePos.y);
        const isSel = selId === `ann_${ann.id}`;
        const col   = isSel ? "#e8a838" : "#94a3b8";
        const fs    = s * Math.max(ann.fontSize || 10, 6);
        const chW   = fs * 0.6; // approx char width
        const boxW  = Math.max(ann.text?.length || 1, 1) * chW + s * 6;
        const boxH  = fs + s * 5;

        return (
          <g key={ann.id} style={{ cursor: dragAnnId === `ann_${ann.id}` ? 'grabbing' : 'grab' }}
            onClick={ev => { ev.stopPropagation(); onSelect?.("ann_" + ann.id); }}
            onMouseDown={ev => {
              ev.stopPropagation();
              ev.preventDefault();
              if (onDragStart) onDragStart("ann_" + ann.id);
            }}>
            {/* Leader lines */}
            {(ann.leaderPts || []).length > 0 && (
              <polyline
                points={[...ann.leaderPts.map(p => `${w2p(p.x)},${w2p(p.y)}`), `${ax},${ay}`].join(" ")}
                fill="none" stroke={col} strokeWidth={s * 0.6} opacity={0.7}/>
            )}
            {/* Invisible hit target */}
            <rect x={ax - s*2} y={ay - boxH} width={boxW + s*4} height={boxH + s*4}
              fill="transparent" stroke="none"/>
            {/* Callout box */}
            {ann.style === "callout" ? (
              <>
                <rect x={ax - s*2} y={ay - boxH + s*2} width={boxW} height={boxH - s*2}
                  rx={s} fill="#020a14" stroke={col} strokeWidth={s * 0.5} opacity={0.9}/>
                <text x={ax + s*1} y={ay + s*2} fontSize={fs}
                  fontFamily={FONT} fill={col}
                  style={{ pointerEvents:"none" }}>
                  {ann.text}
                </text>
              </>
            ) : (
              <text x={ax} y={ay} fontSize={fs}
                fontFamily={FONT} fill={col}
                style={{ pointerEvents:"none" }}>
                {ann.text}
              </text>
            )}
          </g>
        );
      })}
    </g>
  );
}
